package rendering

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

private val COLOR_BADGE_STAR   = Color(0xFFFFD600)   // gold star
private val COLOR_BADGE_INNER  = Color(0xFFFF6D00)   // orange inner circle
private val COLOR_BADGE_NUMBER = Color(0xFF000000)   // black number — readable on gold
private val COLOR_B2B_LABEL    = Color(0xFF888888)   // muted "B2B" label

/**
 * Animated sunburst badge showing the current B2B (back-to-back) chain count.
 *
 * Layout: `B2B` label on the left, spinning star badge on the right.
 * The badge spins slowly at idle and bursts to a fast spin when [b2bCount]
 * increments. Only the count number is shown inside the badge (in black so it
 * reads clearly against the gold star).
 *
 * @param b2bCount  Current back-to-back chain count. Hidden when ≤ 0.
 * @param size      Diameter of the badge canvas.
 * @param modifier  Applied to the outer [Row] container.
 */
@Composable
fun SunburstBadge(
    b2bCount: Int,
    size: Dp = 40.dp,
    modifier: Modifier = Modifier,
) {
    if (b2bCount <= 0) return

    val rotation = remember { Animatable(0f) }

    // Idle spin: one full rotation every 12 seconds
    LaunchedEffect(Unit) {
        while (true) {
            rotation.animateTo(
                targetValue = rotation.value + 360f,
                animationSpec = tween(durationMillis = 12_000, easing = LinearEasing),
            )
            rotation.snapTo(rotation.value % 360f)
        }
    }

    // Burst spin: fast extra rotation on every B2B increment
    LaunchedEffect(b2bCount) {
        if (b2bCount > 0) {
            rotation.animateTo(
                targetValue = rotation.value + 360f,
                animationSpec = tween(durationMillis = 350, easing = LinearEasing),
            )
        }
    }

    Row(
        modifier = modifier,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        // "B2B" label to the left of the badge
        Text(
            text = "B2B",
            style = TextStyle(
                fontFamily = fontMono(),
                fontSize = 9.sp,
                color = COLOR_B2B_LABEL,
                letterSpacing = 0.1.sp,
            ),
        )

        Spacer(modifier = Modifier.width(4.dp))

        // Spinning star badge with count number inside
        Box(
            modifier = Modifier.size(size),
            contentAlignment = Alignment.Center,
        ) {
            Canvas(modifier = Modifier.size(size)) {
                // Rotating star
                rotate(rotation.value, pivot = center) {
                    drawSunburst(
                        center = center,
                        outerRadius = size.toPx() * 0.48f,
                        innerRadius = size.toPx() * 0.28f,
                        spikes = 12,
                        color = COLOR_BADGE_STAR,
                    )
                }
                // Static inner circle (doesn't rotate — keeps number readable)
                drawCircle(
                    color = COLOR_BADGE_INNER,
                    radius = size.toPx() * 0.24f,
                    center = center,
                )
            }

            // Count number — black so it reads clearly on the orange/gold badge
            Text(
                text = b2bCount.toString(),
                style = TextStyle(
                    fontFamily = fontMono(),
                    fontSize = (size.value * 0.32f).sp,
                    color = COLOR_BADGE_NUMBER,
                    letterSpacing = 0.sp,
                ),
                textAlign = TextAlign.Center,
            )
        }
    }
}

/**
 * Draws a sunburst (star polygon) using alternating outer and inner radii.
 */
private fun DrawScope.drawSunburst(
    center: Offset,
    outerRadius: Float,
    innerRadius: Float,
    spikes: Int,
    color: Color,
) {
    val path = Path()
    val totalPoints = spikes * 2
    val angleStep = (2.0 * PI / totalPoints).toFloat()
    val startAngle = (-PI / 2).toFloat()

    for (i in 0 until totalPoints) {
        val angle = startAngle + i * angleStep
        val radius = if (i % 2 == 0) outerRadius else innerRadius
        val x = center.x + radius * cos(angle)
        val y = center.y + radius * sin(angle)
        if (i == 0) path.moveTo(x, y) else path.lineTo(x, y)
    }
    path.close()
    drawPath(path = path, color = color)
}
